import React, { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import UserPanelCategoryCarousel from './UserPanelCategoryCarousel';
import UserPanelContentSkeleton from './UserPanelContentSkeleton';
import usePosterImageState from './hooks/usePosterImageState';
import { matchesTurkishSearch, normalizeTurkishSearchText } from './utils/turkishSearch';
import { SERIES_PLATFORMS } from './userPanelSeriesData';
import {
  formatSeriesCount,
  resolveKnownSeriesPlatformId,
  selectDailyPopularSeries,
  selectRecentlyAddedSeries,
  sortSeriesNewestFirst,
  sortSeriesPlatformsByImportance,
} from './userPanelSeriesOrdering';
import './userPanelSeries.css';

const SERIES_BATCH_SIZE = 30;
const KNOWN_PLATFORM_MAP = new Map(SERIES_PLATFORMS.map((platform) => [platform.id, platform]));

const normalizeInitialPlatform = (value) => {
  const platformId = String(value || '').trim().toLocaleLowerCase('tr-TR');
  return KNOWN_PLATFORM_MAP.has(platformId) ? platformId : 'all';
};

const getPlatformDisplayName = (platform) => (
  platform.id === 'amazon' ? 'Prime Video' : platform.name
);

const getCommentCount = (item) => {
  const value = item.yorum_sayisi ?? item.commentCount ?? item.comments?.length ?? 0;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.floor(numericValue) : 0;
};

function SeriesPoster({ item }) {
  const posterState = usePosterImageState(item.poster);
  const hasPoster = !posterState.isFailed;

  return (
    <span
      ref={posterState.hostRef}
      className={`tvup-series-library-poster${item.posterFit === 'contain' ? ' is-contain' : ''}${hasPoster ? ' has-poster' : ''}${posterState.isLoaded ? ' is-loaded' : ' is-loading'}`}
      style={{ '--tvup-series-card-rgb': item.platformRgb }}
      aria-busy={posterState.isLoading ? 'true' : undefined}
    >
      {hasPoster && posterState.shouldRequest ? (
        <img
          src={posterState.posterSource}
          alt=""
          loading="eager"
          decoding="async"
          referrerPolicy="no-referrer"
          onLoad={posterState.handleLoad}
          onError={posterState.handleError}
        />
      ) : !hasPoster ? (
        <span className="tvup-series-library-poster-fallback" aria-hidden="true">
          <MovieFilterRoundedIcon />
        </span>
      ) : null}
    </span>
  );
}

function SeriesCard({ item, onSelect, onComments }) {
  const interactive = typeof onSelect === 'function';
  const commentsInteractive = typeof onComments === 'function';

  return (
    <article className={`tvup-series-card${interactive || commentsInteractive ? ' is-interactive' : ''}`}>
      {interactive && (
        <button
          type="button"
          className="tvup-series-card-open-hit"
          onClick={() => onSelect(item.raw || item)}
          aria-label={`${item.name} dizi detaylarını aç`}
          aria-haspopup="dialog"
          aria-controls="tvup-content-detail-modal"
        />
      )}
      <SeriesPoster item={item} />
      <span className="tvup-series-card-copy">
        <b title={item.name}>{item.name}</b>
        <span className="tvup-series-card-engagement">
          <span className="tvup-series-rating" aria-label={`${item.rating || 'Puan yok'} puan`}>
            <StarRoundedIcon aria-hidden="true" />
            <b>{item.rating || '—'}</b>
          </span>
          {commentsInteractive ? (
            <button
              type="button"
              className="tvup-series-comments-button"
              onClick={() => onComments(item)}
              aria-label={`${item.name} dizisi için ${item.commentCount} yorumu aç`}
              aria-haspopup="dialog"
              aria-controls="tvup-comments-modal"
            >
              <ChatBubbleOutlineRoundedIcon aria-hidden="true" />
              {item.commentCount} Yorum
            </button>
          ) : (
            <span className="tvup-series-comments-count">
              <ChatBubbleOutlineRoundedIcon aria-hidden="true" />
              {item.commentCount} Yorum
            </span>
          )}
        </span>
      </span>
    </article>
  );
}

function UserPanelSeries({ series = [], loading = false, initialPlatform = null, onSeriesSelect, onSeriesComments }) {
  const [activePlatform, setActivePlatform] = useState(() => normalizeInitialPlatform(initialPlatform));
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(SERIES_BATCH_SIZE);

  const normalizedSeries = useMemo(() => series.map((item, index) => {
    const rawPlatformId = String(item.platformId || item.platform_id || item.category_id || 'tivigo');
    const suppliedPlatformName = item.platformName
      || item.platform
      || item.category_name
      || '';
    const platformId = resolveKnownSeriesPlatformId(suppliedPlatformName)
      || resolveKnownSeriesPlatformId(rawPlatformId)
      || rawPlatformId;
    const knownPlatform = KNOWN_PLATFORM_MAP.get(platformId);
    const platformName = suppliedPlatformName
      || (knownPlatform ? getPlatformDisplayName(knownPlatform) : '')
      || 'Tivigo';
    return {
      raw: item,
      id: String(item.id || item.series_id || `${platformId}-${index}`),
      name: String(item.name || item.title || 'İsimsiz dizi'),
      poster: item.poster || item.cover || '',
      genre: item.genre || item.meta || item.category_name || 'Dizi',
      rating: item.imdbRating || item.imdb_rating || item.rating || '',
      commentCount: getCommentCount(item),
      added: item.added || 0,
      series_id: item.series_id || item.id,
      recentlyAddedRank: item.recentlyAddedRank ?? null,
      platformId,
      platformName,
      platformColor: item.platformColor || knownPlatform?.color || '#ff8a1f',
      platformRgb: item.platformRgb || knownPlatform?.rgb || '255, 138, 31',
      posterFit: item.posterFit || 'cover',
    };
  }).filter((item) => Boolean(item.poster)), [series]);

  const platformOptions = useMemo(() => {
    const knownOptions = SERIES_PLATFORMS.map((platform) => {
      const matchingSeries = normalizedSeries.filter((item) => item.platformId === platform.id);
      if (!matchingSeries.length) return null;

      return {
        id: platform.id,
        name: getPlatformDisplayName(platform),
        color: platform.color,
        rgb: platform.rgb,
        image: matchingSeries[0].poster,
        count: matchingSeries.length,
      };
    }).filter(Boolean);

    const knownIds = new Set(knownOptions.map((platform) => platform.id));
    const extraOptions = [];

    normalizedSeries.forEach((item) => {
      if (knownIds.has(item.platformId) || extraOptions.some((platform) => platform.id === item.platformId)) return;
      const matchingSeries = normalizedSeries.filter((seriesItem) => seriesItem.platformId === item.platformId);
      extraOptions.push({
        id: item.platformId,
        name: item.platformName,
        color: item.platformColor,
        rgb: item.platformRgb,
        image: matchingSeries[0]?.poster,
        count: matchingSeries.length,
      });
    });

    return sortSeriesPlatformsByImportance([...knownOptions, ...extraOptions]);
  }, [normalizedSeries]);

  const recentlyAddedSeries = useMemo(
    () => selectRecentlyAddedSeries(normalizedSeries),
    [normalizedSeries]
  );

  const popularSeries = useMemo(
    () => selectDailyPopularSeries(normalizedSeries),
    [normalizedSeries]
  );

  const platformTabs = useMemo(() => ([
    {
      id: 'all',
      name: 'Yeni Eklenenler',
      color: '#ff8a1f',
      rgb: '255, 138, 31',
      image: null,
      count: recentlyAddedSeries.length,
    },
    {
      id: 'popular',
      name: 'Bugünün Popüler',
      color: '#ff8a1f',
      rgb: '255, 138, 31',
      image: popularSeries[0]?.poster || null,
      count: popularSeries.length,
    },
    ...platformOptions,
  ]), [platformOptions, popularSeries, recentlyAddedSeries.length]);

  const filteredSeries = useMemo(() => {
    const normalizedQuery = normalizeTurkishSearchText(query);
    const platformSeries = normalizedQuery
      ? sortSeriesNewestFirst(normalizedSeries)
      : activePlatform === 'all'
        ? recentlyAddedSeries
        : activePlatform === 'popular'
          ? popularSeries
          : sortSeriesNewestFirst(
            normalizedSeries.filter((item) => item.platformId === activePlatform)
          );

    return platformSeries.filter((item) => {
      const searchableText = `${item.name} ${item.genre} ${item.platformName}`;
      return matchesTurkishSearch(searchableText, normalizedQuery);
    });
  }, [activePlatform, normalizedSeries, popularSeries, query, recentlyAddedSeries]);

  const displayedSeries = useMemo(
    () => filteredSeries.slice(0, visibleCount),
    [filteredSeries, visibleCount]
  );

  const activePlatformLabel = platformTabs.find((platform) => platform.id === activePlatform)?.name || 'Yeni Eklenenler';
  const hasActiveSearch = query.trim().length > 0;

  useEffect(() => {
    setVisibleCount(SERIES_BATCH_SIZE);
  }, [activePlatform, query]);

  useEffect(() => {
    setActivePlatform(normalizeInitialPlatform(initialPlatform));
    setQuery('');
  }, [initialPlatform]);

  useEffect(() => {
    if (loading) return;

    if (!['all', 'popular'].includes(activePlatform)
      && !platformOptions.some((platform) => platform.id === activePlatform)) {
      setActivePlatform('all');
    }
  }, [activePlatform, loading, platformOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const consumeHeaderSearch = () => {
      let storedSearch = null;

      try {
        storedSearch = window.sessionStorage.getItem('seriesSearchQuery');
        if (!storedSearch) return;
        window.sessionStorage.removeItem('seriesSearchQuery');

        let nextQuery = storedSearch;
        try {
          const parsedSearch = JSON.parse(storedSearch);
          nextQuery = parsedSearch?.query || '';
        } catch (error) {
          // Eski düz metin formatı da desteklenir.
        }

        if (nextQuery) {
          setActivePlatform('all');
          setQuery(String(nextQuery));
        }
      } catch (error) {
        // Session storage kapalıysa sayfa içi arama çalışmaya devam eder.
      }
    };

    consumeHeaderSearch();
    window.addEventListener('seriesSearchUpdated', consumeHeaderSearch);
    return () => window.removeEventListener('seriesSearchUpdated', consumeHeaderSearch);
  }, []);

  const selectPlatform = (platformId) => {
    setActivePlatform(platformId);
    setQuery('');
    setVisibleCount(SERIES_BATCH_SIZE);
  };

  const handlePlatformKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const lastIndex = platformTabs.length - 1;
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? lastIndex
        : event.key === 'ArrowRight'
          ? (index + 1) % platformTabs.length
          : (index - 1 + platformTabs.length) % platformTabs.length;

    selectPlatform(platformTabs[nextIndex].id);
    const buttons = event.currentTarget.parentElement?.querySelectorAll('[role="tab"]');
    buttons?.[nextIndex]?.focus();
  };

  if (loading && normalizedSeries.length === 0) {
    return <UserPanelContentSkeleton variant="series" />;
  }

  return (
    <div className="tvup-series-page">
      <section className="tvup-series-platform-section" aria-labelledby="tvup-series-platform-title">
        <div className="tvup-series-section-heading">
          <div>
            <h2 id="tvup-series-platform-title">Bugün Hangi Diziyi İzlemek İstersin?</h2>
          </div>
          <p>Platforma göre keşfet veya aradığın diziyi bul.</p>
        </div>

        <UserPanelCategoryCarousel
          id="tvup-series-platform-tabs"
          className="tvup-series-platforms"
          ariaLabel="Dizi platformları"
          previousLabel="Dizi kategorilerini sola kaydır"
          nextLabel="Dizi kategorilerini sağa kaydır"
        >
          {platformTabs.map((platform, index) => {
            const isActive = activePlatform === platform.id;
            return (
              <button
                key={platform.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="tvup-series-library-panel"
                tabIndex={isActive ? 0 : -1}
                className={`${isActive ? 'is-active ' : ''}${platform.id === 'all' ? 'is-all' : ''}`.trim()}
                style={{ '--tvup-series-platform-rgb': platform.rgb }}
                onClick={() => selectPlatform(platform.id)}
                onKeyDown={(event) => handlePlatformKeyDown(event, index)}
              >
                {platform.image && <img src={platform.image} alt="" loading="lazy" />}
                <span className="tvup-series-platform-shade" />
                <span className="tvup-series-platform-copy">
                  <small>{platform.name}</small>
                  <b>{formatSeriesCount(platform.count)} dizi</b>
                </span>
              </button>
            );
          })}
        </UserPanelCategoryCarousel>
      </section>

      <section
        id="tvup-series-library-panel"
        className="tvup-series-library"
        role="tabpanel"
        aria-labelledby="tvup-series-platform-title"
      >
        <div className="tvup-series-toolbar">
          <div>
            <h2>{hasActiveSearch ? 'Dizi Arama Sonuçları' : activePlatform === 'all' ? 'Yeni Eklenen Diziler' : activePlatformLabel}</h2>
            <p aria-live="polite">{formatSeriesCount(filteredSeries.length)} dizi gösteriliyor</p>
          </div>
          <label className="tvup-series-search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="search"
              value={query}
              aria-label="Dizi ara"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>

        {displayedSeries.length > 0 ? (
          <div className="tvup-series-grid">
            {displayedSeries.map((item) => (
              <SeriesCard
                key={item.id}
                item={item}
                onSelect={onSeriesSelect}
                onComments={onSeriesComments}
              />
            ))}
          </div>
        ) : (
          <div className="tvup-series-empty" role="status">
            <MovieFilterRoundedIcon />
            <b>Bu aramayla eşleşen dizi bulunamadı.</b>
            <span>Farklı bir dizi adı deneyebilir veya platformu değiştirebilirsin.</span>
          </div>
        )}

        {visibleCount < filteredSeries.length && (
          <button
            type="button"
            className="tvup-series-load-more"
            onClick={() => setVisibleCount((current) => current + SERIES_BATCH_SIZE)}
          >
            <AddRoundedIcon /> Daha fazla göster
            <small>{formatSeriesCount(filteredSeries.length - visibleCount)} dizi kaldı</small>
          </button>
        )}
      </section>
    </div>
  );
}

export default UserPanelSeries;
